import { MouseEvent } from 'react';
import { DocumentItem } from '../types/content';

const FILE_EXTENSION =
  /\.(pdf|docx?|xlsx?|pptx?|txt|rtf|csv|zip|rar|7z|png|jpe?g|gif|webp|svg|bmp|mp3|wav|mp4|webm|mov|m4v|avi|mkv)$/i;

export function normalizeDocText(value?: string) {
  return (value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/\s+/g, ' ')
    .trim();
}

// Dokumanlarin basligi API'de dosya adindan geliyor (ornek: "EYS_PL.pdf").
// Kartlarda uzanti ve alt tire gosterilmemesi icin okunabilir hale getirilir.
export function getDocDisplayTitle(doc: DocumentItem) {
  const raw = (doc.title || '').trim();
  if (!raw) return 'Doküman';

  const withoutExtension = raw.replace(FILE_EXTENSION, '').replace(/[_]+/g, ' ');
  // "Adana-Port-Tehlikeli" gibi tamamen tire ile yazilmis adlarda tireler bosluga
  // cevrilir; "ISO 9001 - Kalite" gibi adlarda tire korunur.
  const readable = (withoutExtension.includes(' ')
    ? withoutExtension
    : withoutExtension.replace(/-+/g, ' ')
  )
    .replace(/\s+/g, ' ')
    .trim();

  return readable || raw;
}

// Arac Rezervasyon Kilavuzu gibi kayitlar video olarak geliyor; tiklaninca
// tarayicida oynatilmasi yerine dosya olarak inmesi isteniyor.
export function isVideoDoc(doc: DocumentItem) {
  const text = normalizeDocText(`${doc.title} ${doc.category || ''} ${doc.icon || ''}`);
  return (
    text.includes('video') ||
    text.includes('izle') ||
    text.includes('rezervasyon') ||
    /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(doc.title || '')
  );
}

// Baslikta uzanti yoksa contentType'tan (ornek: "video/mp4") tureterek ekler.
export function getDocFileName(doc: DocumentItem) {
  // Yuklenen dosyanin gercek adi biliniyorsa o kullanilir; kart basligi sadece yedek.
  // Aksi halde karta yeni dosya baglandiginda indirilen dosya eski adla kaydedilir.
  const base = (doc.fileName || doc.title || 'dokuman').trim().replace(/[\\/:*?"<>|]+/g, '-');
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;

  const mime = (doc.icon || doc.category || '').toLowerCase();
  const match = mime.match(/([a-z]+)\/([a-z0-9.+-]+)/);
  const ext = match ? match[2].replace(/^x-/, '') : isVideoDoc(doc) ? 'mp4' : 'pdf';
  return `${base}.${ext}`;
}

function triggerDownload(href: string, fileName: string) {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Port_Api'nin dosya ucu (/api/dokumanlar/{id}/goster) Content-Disposition: attachment ve
// dosya adini kendisi gonderir; tarayici indirmeyi dogrudan baslatir ve ilerlemeyi gosterir.
export function isApiDocumentUrl(url?: string) {
  return /\/api\/dokumanlar\/\d+\/goster(?:[?#]|$)/i.test(url || '');
}

// download ozniteligi farkli origin'deki dosyalarda yok sayildigi icin dis adresler once
// blob olarak cekilir; CORS engellenirse tarayicinin varsayilan davranisina birakilir.
// API adresleri icin fetch+blob yapilmaz: aksi halde indirme tum dosya bellege inene kadar
// gorunmuyordu (35 MB videoda dakikalarca "hic bir sey olmuyor" izlenimi).
export async function downloadDoc(event: MouseEvent<HTMLAnchorElement>, doc: DocumentItem) {
  const url = doc.fileUrl;
  if (!url || url === '#') return;
  if (isApiDocumentUrl(url)) return;

  event.preventDefault();
  const fileName = getDocFileName(doc);

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Dokuman indirilemedi: ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    triggerDownload(objectUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch {
    triggerDownload(url, fileName);
  }
}
